import { useLayoutEffect, useState } from 'react';
import { getService } from 'react-in-angularjs';

import { getRouteParam, setRouteParams } from '../utils/angularUtils';

type UseRouteParamOptions<T extends string | undefined> = {
  defaultValue?: T;
};
/**
 * Syncs a query parameter with React state. Requires `reloadOnSearch: false` in route config.
 *
 * Uses `replace: true` when setting defaultValue to avoid pushing extra history entries.
 * Without it, navigating to a page triggers a push for the default param,
 * and pressing Back removes the param but the hook re-adds it — creating an infinite loop.
 */
export const useRouteParam = <T extends string | undefined>(
  paramName: string,
  options?: UseRouteParamOptions<T>,
) => {
  const { defaultValue } = options || {};

  const [paramValue, setParamValue] = useState<T>(
    getRouteParam(paramName) || defaultValue,
  );

  const $rootScope = getService('$rootScope');

  const updateParamValue = () => {
    const newValue = getRouteParam(paramName);

    if (newValue) {
      setParamValue(newValue);
    } else if (defaultValue) {
      // will trigger $routeUpdate event and set default value
      setRouteParams({ [paramName]: defaultValue }, { replace: true });
    } else {
      // omit retrun previous value if undefined and defaultValue is not provided
      setParamValue(newValue);
    }
  };

  useLayoutEffect(() => {
    // If no value exists and we have a defaultValue, set it in the URL
    if (!getRouteParam(paramName) && defaultValue) {
      setRouteParams({ [paramName]: defaultValue }, { replace: true });
    }
  }, [paramName, defaultValue]);

  useLayoutEffect(() => {
    // Subscribe to route updates (for query parameter changes when reloadOnSearch: false)
    const unsubscribeRouteUpdate = $rootScope.$on('$routeUpdate', updateParamValue);

    return () => {
      unsubscribeRouteUpdate();
    };
  }, [paramName]);

  return paramValue;
};
